import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../utils/firebase/firebase.utils';
import { selectUser, selectRecentlyViewed } from '../../../../store/user/user.selector';
import { updateRecentlyViewed } from '../../../../store/user/user.action';
import { useSystemInitialization } from '../../../../hooks/useSystemInitialization';
import {
  getAvailableSeries,
  checkChecklistUnlockStatus,
  getAvailableSports,
  getAvailableYears,
  getAvailableManufacturers
} from '../../../../utils/firebase/firebase.utils';
import {
  setChecklistSportDefault,
  setChecklistYearDefault,
  setChecklistManufacturerDefault,
  setChecklistSeriesDefault,
  setChecklistCardTypeDefault,
  setChecklistCardTypeCategoryDefault
} from '../../../../store/system/system.action';

// styles
import {
  SeriesSelectionContainer,
  DropdownsContainer,
  SubFilterContainer,
  SearchContinaer,
  NoUnlockedContainer,
  RecentChecklistsRow,
  RecentChecklistsLabel,
  RecentChecklistsScroll,
  ErrorMessageContainer
} from './SeriesSelection.styles';

// components
import CardAttribute from '../../../../components/card-attribute/CardAttribute';
import ChecklistPanel from '../checklist-panel/ChecklistPanel';
import InputDropdown from '../../../../components/input-dropdown/InputDropdown';
import InputBar from '../../../../components/input-bar/InputBar';
import LoadingIcon from '../../../../components/loading-icon/LoadingIcon';
import PrimaryBtn from '../../../../components/btns/primary-btn/PrimaryBtn';
import Toggle from '../../../../components/toggle/Toggle';

export default function SeriesSelection({ viewing = false, viewingUserID = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const recentlyViewed = useSelector(selectRecentlyViewed);
  const params = useParams();
  const { sport: urlSport, year: urlYear, manufacturer: urlManufacturer, series } = params;

  // Get viewingUserID from React Router context (passed by FriendCollectionPage)
  const outletContext = useOutletContext();
  const contextViewingUserID = outletContext?.viewingUserID || null;

  // Extract friendUID from URL pathname since it's a parent route param
  const friendUID = useMemo(() => {
    const match = location.pathname.match(/\/friends\/([^/]+)\/collection/);
    return match ? match[1] : null;
  }, [location.pathname]);

  // Use viewingUserID from context, prop, or friendUID (in that order)
  const targetViewingUserID = viewingUserID || contextViewingUserID || friendUID;

  // Use the system initialization hook to ensure proper values
  const {
    checklistSportDefault,
    checklistYearDefault,
    checklistManufacturerDefault
  } = useSystemInitialization();

  const [availableSeries, setAvailableSeries] = useState([]);
  // Only set showUnlockedOnly to true for showcase viewing, not friend collection viewing
  const [showUnlockedOnly, setShowUnlockedOnly] = useState(viewing && !friendUID);
  const [unlockStatuses, setUnlockStatuses] = useState({});
  const [collectedSeriesData, setCollectedSeriesData] = useState({});
  const [isLoadingSeries, setIsLoadingSeries] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownOptionsSettled, setDropdownOptionsSettled] = useState(false);
  const [dropdownOptionsError, setDropdownOptionsError] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState({
    sports: [],
    years: [],
    manufacturers: []
  });
  const prevSeriesRef = useRef(series);

  // Determine base path based on context
  const getBasePath = useCallback(() => {
    if (friendUID) {
      return `/friends/${friendUID}/collection`;
    }
    return viewing ? '/showcase-add' : '/checklists';
  }, [friendUID, viewing]);

  // Check if we have all 6 parameters after 'checklists'
  const handleSportChange = (selectedSport) => {
    dispatch(setChecklistSportDefault(selectedSport));
    // Navigate to the new sport/year combination
    const basePath = getBasePath();
    navigate(`${basePath}/${selectedSport}/${checklistYearDefault}/${checklistManufacturerDefault}`);
  };

  const handleYearChange = (selectedYear) => {
    dispatch(setChecklistYearDefault(selectedYear));
    // Navigate to the new sport/year combination
    const basePath = getBasePath();
    navigate(`${basePath}/${checklistSportDefault}/${selectedYear}/${checklistManufacturerDefault}`);
  };

  const handleManufacturerChange = (selectedManufacturer) => {
    dispatch(setChecklistManufacturerDefault(selectedManufacturer));
    // Navigate to the new sport/year/manufacturer combination
    const basePath = getBasePath();
    navigate(`${basePath}/${checklistSportDefault}/${checklistYearDefault}/${selectedManufacturer}`);
  };

  useEffect(() => {
    const prevSeries = prevSeriesRef.current;

    if (!series) {
      dispatch(setChecklistSeriesDefault(null));
      dispatch(setChecklistCardTypeDefault(null));
      dispatch(setChecklistCardTypeCategoryDefault(null));
    } else if (series !== prevSeries) {
      dispatch(setChecklistSeriesDefault(series));
      dispatch(setChecklistCardTypeDefault(null));
      dispatch(setChecklistCardTypeCategoryDefault(null));
    }

    prevSeriesRef.current = series;
  }, [dispatch, series]);

  // Use Redux values if URL params are not available (for checklist view)
  // Add fallback values in case Redux state is not yet initialized or undefined
  // Also handle the case where URL params might be the string 'undefined'
  const sport = (urlSport && urlSport !== 'undefined') ? urlSport : checklistSportDefault || "MLB";
  const year = (urlYear && urlYear !== 'undefined') ? urlYear : checklistYearDefault || "2025";
  const manufacturer = (urlManufacturer && urlManufacturer !== 'undefined') ? urlManufacturer : checklistManufacturerDefault || "Topps";

  // Get unlocked combinations for dropdown filtering (use friend's UID when viewing friend's collection)
  const getUnlockedCombinations = useCallback(async () => {
    const userIdForUnlockCheck = targetViewingUserID || user?.uid;
    if (!userIdForUnlockCheck) return { sports: [], years: [], manufacturers: [] };

    try {
      const unlockDocRef = doc(db, `users/${userIdForUnlockCheck}/unlockedChecklists`, 'allUnlocked');
      const unlockSnap = await getDoc(unlockDocRef);

      if (!unlockSnap.exists()) {
        return { sports: [], years: [], manufacturers: [] };
      }

      const unlockData = unlockSnap.data();
      const sports = new Set();
      const years = new Set();
      const manufacturers = new Set();

      Object.values(unlockData).forEach(item => {
        if (item.isUnlocked) {
          sports.add(item.sport);
          years.add(item.year);
          manufacturers.add(item.manufacturer);
        }
      });

      return {
        sports: Array.from(sports),
        years: Array.from(years),
        manufacturers: Array.from(manufacturers)
      };
    } catch (error) {
      throw error;
    }
  }, [targetViewingUserID, user?.uid]);

  // Step 1: Fetch dropdown options first (must complete before series fetch)
  useEffect(() => {
    setDropdownOptionsSettled(false);

    const fetchDropdownOptions = async () => {
      const useUnlockedMode = targetViewingUserID || viewing || showUnlockedOnly;

      if (useUnlockedMode) {
        try {
          const unlockedCombos = await getUnlockedCombinations();
          setFilteredOptions({
            sports: unlockedCombos.sports || [],
            years: unlockedCombos.years || [],
            manufacturers: unlockedCombos.manufacturers || []
          });
          setDropdownOptionsError(false);
        } catch (error) {
          setFilteredOptions({ sports: [], years: [], manufacturers: [] });
          setDropdownOptionsError(true);
        }
      } else {
        try {
          const [sportsData, yearsData, manufacturersData] = await Promise.all([
            getAvailableSports(null),
            sport ? getAvailableYears(null, sport) : Promise.resolve([]),
            sport && year ? getAvailableManufacturers(null, sport, year) : Promise.resolve([])
          ]);

          const sportsList = Array.isArray(sportsData)
            ? sportsData.map((d) => (typeof d === 'string' ? d : d.sport))
            : Object.keys(sportsData || {});
          const yearsList = Array.isArray(yearsData)
            ? yearsData
            : typeof yearsData === 'object' && yearsData !== null
              ? Object.keys(yearsData)
              : [];
          const manufacturersList = Array.isArray(manufacturersData)
            ? manufacturersData.map((d) => (typeof d === 'string' ? d : d.manufacturer))
            : [];

          const options = {
            sports: sportsList.length ? sportsList.sort() : [],
            years: yearsList.length ? yearsList.sort((a, b) => b.localeCompare(a)) : [],
            manufacturers: manufacturersList.length ? manufacturersList.sort() : []
          };
          setFilteredOptions(options);
          setDropdownOptionsError(false);
        } catch (error) {
          setFilteredOptions({ sports: [], years: [], manufacturers: [] });
          setDropdownOptionsError(true);
        }
      }

      setDropdownOptionsSettled(true);
    };

    fetchDropdownOptions();
  }, [targetViewingUserID, viewing, showUnlockedOnly, sport, year, getUnlockedCombinations]);

  // Step 2: Fetch series only after dropdown options have settled successfully
  useEffect(() => {
    if (!dropdownOptionsSettled || dropdownOptionsError || !sport || !year || !manufacturer) {
      if (!dropdownOptionsSettled) {
        setIsLoadingSeries(true);
      } else if (dropdownOptionsError) {
        setIsLoadingSeries(false);
      }
      return;
    }

    const fetchSeries = async () => {
      try {
        setIsLoadingSeries(true);
        const seriesData = await getAvailableSeries(null, sport, year, manufacturer, "view");
        const data = seriesData || {};
        setAvailableSeries(data);

        if (Object.keys(data).length === 0 || !user?.uid) {
          setIsLoadingSeries(false);
        }
      } catch (error) {
        setAvailableSeries({});
        setIsLoadingSeries(false);
      }
    };

    fetchSeries();
  }, [dropdownOptionsSettled, dropdownOptionsError, sport, year, manufacturer, user?.uid]);

  // Check unlock status for each checklist (use friend's UID when viewing friend's collection)
  useEffect(() => {
    const checkUnlockStatuses = async () => {
      const statuses = {};

      if (!availableSeries || typeof availableSeries !== 'object' || Object.keys(availableSeries).length === 0) {
        setUnlockStatuses({});
        return;
      }

      // Determine which user ID to use for unlock checks
      const userIdForUnlockCheck = targetViewingUserID || user?.uid;

      if (!userIdForUnlockCheck) {
        setUnlockStatuses({});
        return;
      }

      setIsLoadingSeries(true);
      for (const seriesKey of Object.keys(availableSeries)) {
        const unlockStatus = await checkChecklistUnlockStatus(
          userIdForUnlockCheck,
          sport,
          year,
          manufacturer,
          seriesKey
        );
        statuses[seriesKey] = unlockStatus;
      }

      setUnlockStatuses(statuses);
      setIsLoadingSeries(false);
    };

    if (availableSeries && Object.keys(availableSeries).length > 0) {
      checkUnlockStatuses();
    } else {
      setUnlockStatuses({});
    }
  }, [targetViewingUserID, user?.uid, availableSeries, sport, year, manufacturer]);

  // Fetch collected series data for friend when viewing friend's collection
  useEffect(() => {
    const fetchCollectedSeriesData = async () => {
      if (!targetViewingUserID || !availableSeries || Object.keys(availableSeries).length === 0) {
        setCollectedSeriesData({});
        return;
      }

      const collectedData = {};

      // Only fetch for unlocked series
      for (const seriesKey of Object.keys(availableSeries)) {
        const isUnlocked = unlockStatuses[seriesKey]?.isUnlocked;
        if (isUnlocked) {
          try {
            const seriesData = await getAvailableSeries(targetViewingUserID, sport, year, manufacturer);
            // seriesData is an object where keys are series names
            if (seriesData && seriesData[seriesKey]) {
              collectedData[seriesKey] = seriesData[seriesKey];
            }
          } catch (error) {
            // Error fetching collected data - skip this series
          }
        }
      }

      setCollectedSeriesData(collectedData);
    };

    if (targetViewingUserID && availableSeries && Object.keys(unlockStatuses).length > 0) {
      fetchCollectedSeriesData();
    } else {
      setCollectedSeriesData({});
    }
  }, [targetViewingUserID, availableSeries, sport, year, manufacturer, unlockStatuses]);

  // Automatically set showUnlockedOnly to true when viewing = true (but not for friend collections)
  useEffect(() => {
    if (viewing && !friendUID) {
      setShowUnlockedOnly(true);
    }
  }, [viewing, friendUID]);

  // Auto-select first available option if current selection is not in filtered options
  // Run when dropdown options have settled and we have options to validate against
  useEffect(() => {
    if (!dropdownOptionsSettled || dropdownOptionsError) return;
    if (!filteredOptions.sports.length && !filteredOptions.years.length && !filteredOptions.manufacturers.length) return;

    const basePath = getBasePath();
    let needsNavigation = false;
    let newSport = checklistSportDefault;
    let newYear = checklistYearDefault;
    let newManufacturer = checklistManufacturerDefault;

    if (filteredOptions.sports.length > 0 && !filteredOptions.sports.includes(checklistSportDefault)) {
      newSport = filteredOptions.sports[0];
      dispatch(setChecklistSportDefault(newSport));
      needsNavigation = true;
    }
    if (filteredOptions.years.length > 0 && !filteredOptions.years.includes(checklistYearDefault)) {
      newYear = filteredOptions.years[0];
      dispatch(setChecklistYearDefault(newYear));
      needsNavigation = true;
    }
    if (filteredOptions.manufacturers.length > 0 && !filteredOptions.manufacturers.includes(checklistManufacturerDefault)) {
      newManufacturer = filteredOptions.manufacturers[0];
      dispatch(setChecklistManufacturerDefault(newManufacturer));
      needsNavigation = true;
    }

    if (needsNavigation) {
      navigate(`${basePath}/${newSport}/${newYear}/${newManufacturer}`);
    }
  }, [dropdownOptionsSettled, dropdownOptionsError, filteredOptions, checklistSportDefault, checklistYearDefault, checklistManufacturerDefault, dispatch, navigate, getBasePath]);

  const basePath = `/${sport}/${year}/${manufacturer}`;
  // Determine the correct base path - check friendUID first, then viewing prop
  const selectedBasePath = useMemo(() => {
    if (friendUID) {
      return `/friends/${friendUID}/collection${basePath}`;
    }
    return viewing ? `/showcase-add${basePath}` : `/checklists${basePath}`;
  }, [friendUID, viewing, basePath]);

  // Filter series based on showUnlockedOnly toggle, friend's unlocked status, and card counts
  const filteredSeries = useMemo(() => {

    // Ensure availableSeries is an object (ChecklistPanel expects object with series names as keys)
    if (!availableSeries || typeof availableSeries !== 'object') {
      return {};
    }

    let seriesToFilter = availableSeries;

    // When viewing a friend's collection, always filter to unlocked series with at least 1 card
    if (targetViewingUserID) {
      const unlockedSeriesWithCards = {};
      Object.entries(availableSeries).forEach(([seriesKey, seriesData]) => {
        const isUnlocked = unlockStatuses[seriesKey]?.isUnlocked;
        const cardCount = collectedSeriesData[seriesKey]?.cardCount || 0;

        // Only include if unlocked AND has at least 1 card
        if (isUnlocked && cardCount > 0) {
          unlockedSeriesWithCards[seriesKey] = seriesData;
        }
      });
      seriesToFilter = unlockedSeriesWithCards;
    }
    // If toggle is on (for own collection), only show unlocked series
    else if (showUnlockedOnly) {
      const unlockedSeries = {};
      Object.entries(availableSeries).forEach(([seriesKey, seriesData]) => {
        // Check if the series is unlocked using unlock status
        const isUnlocked = unlockStatuses[seriesKey]?.isUnlocked;
        if (isUnlocked) {
          unlockedSeries[seriesKey] = seriesData;
        }
      });
      seriesToFilter = unlockedSeries;
    }

    // Filter by search term if provided
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      const filtered = {};
      Object.entries(seriesToFilter).forEach(([seriesKey, seriesData]) => {
        if (seriesKey.toLowerCase().includes(searchLower)) {
          filtered[seriesKey] = seriesData;
        }
      });
      return filtered;
    }

    return seriesToFilter;
  }, [availableSeries, showUnlockedOnly, unlockStatuses, searchTerm, targetViewingUserID, collectedSeriesData]);

  useEffect(() => {
    if (friendUID || recentlyViewed.length > 0 || !Array.isArray(user?.recentChecklists)) return;
    dispatch(updateRecentlyViewed(user.recentChecklists));
  }, [dispatch, friendUID, recentlyViewed.length, user?.recentChecklists]);

  const recentChecklists = useMemo(() => {
    if (friendUID || !Array.isArray(recentlyViewed)) return [];
    return recentlyViewed
      .filter((checklist) => checklist?.sport && checklist?.year && checklist?.manufacturer && checklist?.series)
      .slice(0, 5);
  }, [friendUID, recentlyViewed]);

  const handleRecentChecklistClick = useCallback((checklist) => {
    navigate(`/checklists/${checklist.sport}/${checklist.year}/${checklist.manufacturer}/${checklist.series}`);
  }, [navigate]);

  return (
    <SeriesSelectionContainer>
      {!series &&
        <h1 className="pageHeader">CHECKLISTS</h1>
      }
      {!series && recentChecklists.length > 0 && (
        <RecentChecklistsRow>
          <RecentChecklistsLabel>RECENTLY VIEWED:</RecentChecklistsLabel>
          <RecentChecklistsScroll>
            {recentChecklists.map((checklist) => (
              <CardAttribute
                key={`${checklist.sport}/${checklist.year}/${checklist.manufacturer}/${checklist.series}`}
                text={checklist.series}
                size="L"
                onClick={() => handleRecentChecklistClick(checklist)}
              />
            ))}
          </RecentChecklistsScroll>
        </RecentChecklistsRow>
      )}
      <DropdownsContainer>
        <SearchContinaer>
          <InputBar
            id="series-search"
            label="Search"
            type="text"
            value={searchTerm}
            updateValue={setSearchTerm}
            placeholder='e.g. "Series 1"'
          />
        </SearchContinaer>
          <InputDropdown id="checklist-sport-dropdown" label="Sport" options={filteredOptions.sports} onChange={handleSportChange} value={checklistSportDefault || "MLB"} />
          <InputDropdown id="checklist-year-dropdown" label="Year" options={filteredOptions.years} onChange={handleYearChange} value={checklistYearDefault || "2025"} />
          <InputDropdown id="checklist-manufacturer-dropdown" label="Manufacturer" options={filteredOptions.manufacturers} onChange={handleManufacturerChange} value={checklistManufacturerDefault || "Topps"} />
        {!dropdownOptionsError && (
          <SubFilterContainer>
            {!viewing &&
              <Toggle
                label={"Unlocked Only"}
                onChange={setShowUnlockedOnly}
                checked={showUnlockedOnly}
              />
            }
          </SubFilterContainer>
        )}
      </DropdownsContainer>

      {(!dropdownOptionsSettled || (dropdownOptionsSettled && !dropdownOptionsError && isLoadingSeries)) ? (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000
        }}>
          <LoadingIcon />
        </div>
      ) : dropdownOptionsError ? (
        <ErrorMessageContainer>
          <h3>Error loading checklists, check connection.</h3>
        </ErrorMessageContainer>
      ) : Object.keys(filteredSeries).length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
          <ChecklistPanel
            data={filteredSeries}
            basePath={selectedBasePath}
            sport={sport}
            year={year}
            manufacturer={manufacturer}
            viewingUserID={targetViewingUserID}
          />
        </div>
      ) : (
        <div style={{
          paddingTop: '24px',
        }}>
          {showUnlockedOnly ?
            <NoUnlockedContainer>
              <h4>No unlocked checklists.</h4>
              <PrimaryBtn title="GO TO CHECKLISTS" onClick={() => navigate(`/checklists/${sport}/${year}/${manufacturer}`)} />
            </NoUnlockedContainer>

            :
            <h3>No checklists available.</h3>
          }
        </div>
      )}
    </SeriesSelectionContainer>
  )
}